/* eslint-disable @reatom/unit-naming-rule */
import { Result } from '@blade/std-lib'
import { fetchTradesRequestAction } from '@root/entities/trade/atoms'
import { reatomResource } from '@root/shared/lib'

import { fetchWalletTransfersRequestAction } from './wallet-transfers-requests'

import type { WalletTransfers } from '../model'
import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'
import type { Trade } from '@root/entities/trade'

/**
 * What a wallet did, in the order it did it.
 *
 * Its trades and its money movements are two streams the profile already
 * fetches for their own tabs, and neither answers the question this one
 * does — money arrived and a minute later the buying started. Reading them
 * apart, that is two lists; read together, it is a sequence.
 *
 * Merged here rather than on the server: both pages are already being asked
 * for, and one ordering of two short lists is not worth a round trip or a
 * table.
 *
 * It is a page of each, not a paged feed. Asking for more of one would mean
 * knowing which stream the next event belongs to, and a trader reading this
 * wants the last few minutes, not the last month.
 */
const PAGE = 50

export type Activity =
  | { kind: 'trade'; at: number; trade: Trade.Trade }
  | { kind: 'transfer'; at: number; transfer: WalletTransfers.Transfer }

export const reatomWalletActivity = ({
  walletAddress,
  network,
}: {
  walletAddress: Address
  network: Network.Network
}) =>
  reatomResource(
    async (ctx) => {
      const [trades, moved] = await ctx.schedule(async () =>
        Promise.all([
          fetchTradesRequestAction(ctx, {
            network,
            maker: walletAddress,
            sort: 'desc',
            limit: PAGE,
          }),
          fetchWalletTransfersRequestAction(ctx, { network, walletAddress }),
        ]),
      )

      // Either side failing loses the whole sequence rather than showing half
      // of one and calling it what the wallet did.
      if (Result.isFailure(trades)) {
        return Result.fail(trades.failure)
      }
      if (Result.isFailure(moved)) {
        return Result.fail(moved.failure)
      }

      const events: Array<Activity> = [
        ...trades.success.data.map(
          (trade): Activity => ({
            kind: 'trade',
            at: trade.timestamp.getTime(),
            trade,
          }),
        ),
        ...moved.success.transfers.map(
          (transfer): Activity => ({
            kind: 'transfer',
            at: transfer.at,
            transfer,
          }),
        ),
      ]

      return Result.succeed(events.sort((left, right) => right.at - left.at))
    },
    { name: 'reatomWalletActivity', loadOnConnect: true },
  )
